import React from 'react';

import { Pressable, addWithConfig, makeUclComponent } from '@rbilabs/universal-components';
import { View } from 'react-native';

import { Dictionary } from '@rbi-ctg/frontend';
import { Box } from 'components/ui';
import useShallowMemo from 'hooks/use-shallow-memo';
import { getDesignToken, primitive } from 'styles/constants';
import { ITable } from 'utils/table';

const TableBox = makeUclComponent(Box);
type UclBoxProps = React.ComponentProps<typeof TableBox>;

interface ITableProps<Row extends Dictionary> extends UclBoxProps {
  table: ITable<Partial<Row>>;
  columnOrder?: readonly (keyof Row)[];
  onPressRow?: (r: Row | Partial<Row>) => void;
  getRowId?: (row: Partial<Row>) => string | undefined;
  getRowProps?: (row: Partial<Row>) => UclBoxProps & { ref?: (node: View | null) => void };
}

const TableTag = TableBox.withConfig({
  width: 'full',
  backgroundColor: getDesignToken('background-card'),
  borderColor: primitive.$mayoEggWhite,
  borderStyle: 'solid',
  borderWidth: 1,
  borderRadius: 'xl',
});

const Th = TableBox.withConfig({ flex: 1 });

const Td = TableBox.withConfig({
  height: 'full',
  flexGrow: 1,
});

const Tr = TableBox.withConfig<{ withBorderBottom?: boolean }>(({ withBorderBottom }) => ({
  width: 'full',
  paddingY: '$4',
  flexDirection: 'row',
  borderBottomColor: getDesignToken('border-default'),
  borderStyle: 'solid',
  borderBottomWidth: withBorderBottom ? 1 : 0,
  paddingX: '$4',
}));

const TableComponent = <Row extends Dictionary>({
  table,
  columnOrder: columnOrderProp,
  onPressRow: onPressProp,
  getRowId,
  getRowProps,
  ...boxProps
}: ITableProps<Row>) => {
  /**
   * Make sure all column keys line up between head and body.
   */
  const columnOrder = useShallowMemo(() => {
    const orderedColumnKeys = columnOrderProp || [];
    const firstRow = table.head[0] || table.body[0] || {};
    return Array.from(new Set([...orderedColumnKeys, ...Object.keys(firstRow)]));
  }, [columnOrderProp, table.head[0], table.body[0]]);

  const onPressRow = React.useCallback((row: Row | Partial<Row>) => () => onPressProp?.(row), [
    onPressProp,
  ]);

  return (
    <TableTag {...boxProps}>
      {table.head.map((row, rowIndex, tableHeadItems) => {
        const isLastChild = rowIndex === tableHeadItems.length - 1;
        return (
          <Tr key={`row.${rowIndex}`} withBorderBottom={isLastChild}>
            {columnOrder.map((key, keyIndex) => (
              // @ts-expect-error TS(2731) FIXME: Implicit conversion of a 'symbol' to a 'string' wi... Remove this comment to see the full error message
              <Th key={`${key}.${keyIndex}`}>{row[key]}</Th>
            ))}
          </Tr>
        );
      })}
      {table.body.map((row, rowIndex, tableBodyItems) => {
        const isLastChild = rowIndex === tableBodyItems.length - 1;
        const rowId = getRowId?.(row);
        return (
          <Pressable key={rowId ?? `row.${rowIndex}`} onPress={onPressRow(row)}>
            <Tr {...getRowProps?.(row)} nativeID={rowId} withBorderBottom={!isLastChild}>
              {columnOrder.map((key, keyIndex) => (
                // @ts-expect-error TS(2731) FIXME: Implicit conversion of a 'symbol' to a 'string' wi... Remove this comment to see the full error message
                <Td key={`${key}.${keyIndex}`}>{row[key]}</Td>
              ))}
            </Tr>
          </Pressable>
        );
      })}
    </TableTag>
  );
};

export const Table = addWithConfig(TableComponent);
